import { ReactElement, ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import {
  Area,
  AreaChart,
  DotItemDotProps,
  ReferenceArea,
  useXAxisScale,
  useYAxisScale
} from 'recharts';
import {
  ChartXAxis,
  ChartYAxis,
  ChartGrid as ChartGridLines
} from '@Components/Charts/chart-presets';
import * as Dfns from 'date-fns/fp';
import { Brand60 } from '@src/utils/colors';
import {
  AXIS_TICK_COUNT,
  AXIS_TICK_STYLE,
  ChartGrid,
  ChartMargin,
  SpikeChartData,
  SpotCoordinate
} from './spike-chart-constants';
import { coordinatesEqual, getXAxisTicks, getYAxisDomain } from './spike-chart-utils';

export type DotRenderProps = DotItemDotProps;

interface CoordinateReporterProps {
  chartData: SpikeChartData[];
  onCoordinates: (coordinates: Record<number, SpotCoordinate>) => void;
}

// Reports each spot's on-screen pixel position so a table-row hover (outside the
// chart) can position the custom tooltip at the matching point on the chart.
// Must render as a child of <AreaChart> — recharts' axis-scale hooks only work
// inside chart context.
const CoordinateReporter = ({ chartData, onCoordinates }: CoordinateReporterProps) => {
  const xScale = useXAxisScale();
  const yScale = useYAxisScale();
  const lastCoordinatesRef = useRef<Record<number, SpotCoordinate>>({});

  useEffect(() => {
    if (!xScale || !yScale) {
      return;
    }
    const coordinates: Record<number, SpotCoordinate> = {};
    for (const point of chartData) {
      const x = xScale(point.time);
      const y = yScale(point.kpiCount);
      if (x !== undefined && y !== undefined) {
        coordinates[point.time] = { x, y };
      }
    }
    // recharts recreates xScale/yScale on every render even when unchanged, which
    // would otherwise re-trigger this effect and setState in an infinite loop.
    if (coordinatesEqual(lastCoordinatesRef.current, coordinates)) {
      return;
    }
    lastCoordinatesRef.current = coordinates;
    onCoordinates(coordinates);
  }, [chartData, onCoordinates, xScale, yScale]);

  return null;
};

interface InnerSpikeChartProps {
  bottom?: number | string;
  chartData: SpikeChartData[];
  fillColor?: string;
  fillOpacity?: number;
  grid?: ChartGrid;
  height: number;
  left?: number | string;
  margin?: ChartMargin;
  onCoordinates: (coordinates: Record<number, SpotCoordinate>) => void;
  rechartsToolTip?: ReactElement;
  renderDot?: (props: DotRenderProps) => ReactNode;
  right?: number | string;
  setBottom: (bottom: number) => void;
  setLeft: (left: number | string) => void;
  setRight: (right: number | string) => void;
  setTop: (top: number | string) => void;
  strokeColor?: string;
  top?: number | string;
  width: number;
  xAxisPadding?: { left: number; right: number };
  yAxisTickCount?: number;
  yAxisTitle?: string;
}

export const InnerSpikeChart = ({
  bottom = 0,
  chartData,
  fillColor = Brand60,
  fillOpacity = 0.5,
  grid,
  height,
  left = 'dataMin',
  margin,
  onCoordinates,
  rechartsToolTip,
  renderDot,
  right = 'dataMax',
  setBottom,
  setLeft,
  setRight,
  setTop,
  strokeColor = Brand60,
  top = 'auto',
  width,
  xAxisPadding,
  yAxisTickCount = AXIS_TICK_COUNT,
  yAxisTitle
}: InnerSpikeChartProps) => {
  const xTicks = useMemo(() => getXAxisTicks(chartData, AXIS_TICK_COUNT), [chartData]);
  const yTicks = useMemo(() => {
    if (!chartData.length) {
      return null;
    }
    const maxY = Math.max(...chartData.map(elem => elem.kpiCount));
    return Array.from({ length: yAxisTickCount }, (_, i) =>
      Math.round((i * maxY) / (yAxisTickCount - 1))
    );
  }, [chartData, yAxisTickCount]);

  const [refAreaLeft, setRefAreaLeft] = useState<number>(-1);
  const [refAreaRight, setRefAreaRight] = useState<number>(-1);

  const zoom = () => {
    if (refAreaLeft > -1 && refAreaRight > -1 && refAreaLeft !== refAreaRight) {
      const [newLeft, newRight] =
        refAreaLeft > refAreaRight ? [refAreaRight, refAreaLeft] : [refAreaLeft, refAreaRight];
      const [newBottom, newTop] = getYAxisDomain(chartData, newLeft, newRight);
      setLeft(newLeft);
      setRight(newRight);
      setTop(newTop);
      setBottom(newBottom);
    }
    setRefAreaLeft(-1);
    setRefAreaRight(-1);
  };

  if (!xTicks || !yTicks) {
    return null;
  }

  return (
    <AreaChart
      data={chartData}
      width={width}
      height={height}
      margin={margin}
      onMouseDown={e => setRefAreaLeft(e?.activeLabel != null ? Number(e.activeLabel) : -1)}
      onMouseMove={e => {
        if (refAreaLeft > -1) {
          setRefAreaRight(e?.activeLabel != null ? Number(e.activeLabel) : -1);
        }
      }}
      onMouseUp={() => zoom()}
    >
      {grid && (
        <ChartGridLines
          vertical={grid.vertical}
          stroke={grid.stroke}
          strokeWidth={grid.strokeWidth}
        />
      )}
      <CoordinateReporter chartData={chartData} onCoordinates={onCoordinates} />
      <Area
        type='natural'
        dataKey='kpiCount'
        stroke={strokeColor}
        strokeWidth={2}
        fillOpacity={fillOpacity}
        fill={fillColor}
        isAnimationActive={false}
        activeDot={false}
        dot={renderDot}
      />
      <ChartXAxis
        dataKey='time'
        type='number'
        domain={[left, right]}
        allowDataOverflow
        padding={{ left: xAxisPadding?.left ?? 0, right: xAxisPadding?.right ?? 0 }}
        tickFormatter={time => Dfns.format('MMM do', new Date(time).getTime())}
        tick={AXIS_TICK_STYLE}
        label={{ value: 'Eastern Spot Time', angle: 0, position: 'bottom' }}
        ticks={xTicks}
        tickCount={xTicks.length}
      />
      <ChartYAxis
        domain={[bottom, top]}
        label={yAxisTitle ? { value: yAxisTitle, angle: -90, position: 'insideLeft' } : undefined}
        tick={AXIS_TICK_STYLE}
        allowDataOverflow
        tickCount={yTicks.length}
        ticks={yTicks}
      />
      {rechartsToolTip}
      {refAreaLeft > -1 && refAreaRight > -1 && (
        <ReferenceArea x1={refAreaLeft} x2={refAreaRight} />
      )}
    </AreaChart>
  );
};
